import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, Handshake, Users, Euro, Wallet, Settings2, BarChart3 } from "lucide-react";
import { BpPartnersTab } from "@/components/admin/bp/BpPartnersTab";
import { BpLeadsTab } from "@/components/admin/bp/BpLeadsTab";
import { BpCommissionsTab } from "@/components/admin/bp/BpCommissionsTab";
import { BpPayoutsTab } from "@/components/admin/bp/BpPayoutsTab";
import { BpSettingsTab } from "@/components/admin/bp/BpSettingsTab";
import { BpSalesDashboardTab } from "@/components/admin/bp/BpSalesDashboardTab";
import { BpDetailSheet } from "@/components/admin/bp/BpDetailSheet";
import { eur } from "@/components/bp/EarningsBoard";

const TABS = [["partners", "Partenaires", Handshake], ["sales", "Ventes", BarChart3], ["leads", "Leads", Users], ["commissions", "Commissions", Euro], ["payouts", "Versements", Wallet], ["settings", "Réglages", Settings2]];

export default function AdminBusinessPartners() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "partners");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const bpId = params.get("bp");

  const load = async () => {
    setLoading(true);
    try { const { data: d } = await api.get("/admin/bp"); setData(d); setRefreshKey((k) => k + 1); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const openBp = (id) => { params.set("bp", id); setParams(params, { replace: true }); };
  const closeBp = () => { params.delete("bp"); setParams(params, { replace: true }); };
  const switchTab = (key) => { setTab(key); params.set("tab", key); setParams(params, { replace: true }); };

  const k = data?.kpis;
  const partners = data?.items || [];
  return (
    <div className="min-h-full" data-testid="admin-bp">
      <PageHeader eyebrow="Admin · PAG JOB" title="Business Partners" sub="Comptes PAG-BP, leads, ventes attribuées, commissions fixes par formule validées par PAG, versements mensuels et contrats signés."
                  right={<Button variant="outline" onClick={load} disabled={loading} className="h-11" data-testid="bp-admin-refresh"><RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />Recharger</Button>} />
      <div className="p-6 lg:p-10 space-y-6">
        {k && (
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="bp-admin-kpis">
            {[["Partenaires", k.total, "#002FA7"], ["Actifs", k.active, "#059669"], ["Onboarding", k.onboarding, "#F59E0B"], ["Suspendus", k.suspended, "#E60000"], ["Leads", k.leads_total, "#7C3AED"], ["Gagnés", k.leads_won, "#059669"],
              ["À valider", eur(k.commissions_pending), "#F59E0B"], ["Versé", eur(k.commissions_paid), "#059669"]].map(([l, v, c]) => (
              <div key={l} className="bg-white border border-[#DEE2E6] p-4" data-testid={`bp-kpi-${l.toLowerCase().replace(/\s/g, "-")}`}><div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: c }}>{l}</div><div className="font-display font-black text-2xl xl:text-3xl mt-1 truncate">{v}</div></div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5" role="tablist">
          {TABS.map(([key, label, Icon]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => switchTab(key)} data-testid={`bp-admin-tab-${key}`}
                    className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold px-3 py-2 border transition ${tab === key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] hover:border-[#0A0A0A]"}`}><Icon className="h-3.5 w-3.5" />{label}</button>
          ))}
        </div>
        {tab === "partners" && <BpPartnersTab items={partners} onOpen={openBp} />}
        {tab === "sales" && <BpSalesDashboardTab refreshKey={refreshKey} onOpenBp={openBp} />}
        {tab === "leads" && <BpLeadsTab partners={partners} refreshKey={refreshKey} onChanged={load} />}
        {tab === "commissions" && <BpCommissionsTab partners={partners} refreshKey={refreshKey} onChanged={load} />}
        {tab === "payouts" && <BpPayoutsTab refreshKey={refreshKey} onChanged={load} onOpenBp={openBp} />}
        {tab === "settings" && <BpSettingsTab onChanged={load} onOpenBp={openBp} refreshKey={refreshKey} />}
      </div>
      <BpDetailSheet bpId={bpId} onClose={closeBp} onChanged={load} />
    </div>
  );
}
